import axios from "axios";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import "./Login.css";
import { ACCENT, INPUT_CLASS, SUBMIT_CLASS } from "../Auth/authStyles";
import { SERVERURL } from "../../ServerUrl";

const HEADING = "Log in";
const SIGNUP_PROMPT = "Don't have an account?";
const ADMIN_HEADING = "Admin login";
const ADMIN_PROMPT = "Use your administrator account to continue.";

const Login = ({ adminOnly = false }) => {
  const navigate = useNavigate();
  const [valid, setValid] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Failures render inside the card instead of a popup.
  const [formError, setFormError] = useState("");
  const [user, setUser] = useState({ email: "", password: "" });

  const handleChange = (e) => {
    const { value, name } = e.target;
    setUser({ ...user, [name]: value });
  };

  const loginClicked = async () => {
    if (user.email && user.password) {
      setSubmitting(true);
      try {
        const response = await axios.post(`${SERVERURL}/api/v1/login`, user);

        if (response.data.message !== "Login Successful") {
          return;
        }

        if (response.data.category === "admin") {
          localStorage.setItem("token", response?.data?.token);
          localStorage.setItem("admin", response?.data?.User?.fullName);
          localStorage.setItem("adminEmail", response?.data?.User?.email);
          localStorage.removeItem("loggedin");

          navigate("/admin/users");
          return;
        }

        if (adminOnly) {
          setFormError("This account does not have admin access.");
          return;
        }

        if (response.data.category !== "admin") {
          localStorage.setItem("loggedin", true);
          localStorage.setItem("token", response?.data?.token);
          localStorage.removeItem("admin");

          navigate("/");
        }
      } catch (error) {
        console.error(error.response);
        setFormError(
          error.response?.data?.message || "Something went wrong. Try again."
        );
      } finally {
        setSubmitting(false);
      }
    } else {
      setValid(2);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    loginClicked();
  };

  return (
    <div className="w-full bg-white relative overflow-hidden">
      <div className="relative z-10 max-w-xl mx-auto px-4 md:px-10 pt-4 pb-10 md:pt-5 md:pb-16">
        <div className="rounded-3xl border border-black/10 bg-white p-3 md:p-4 shadow-[0_40px_80px_-32px_rgba(27,10,68,0.22)]">
          <div className="flex flex-col !flex-nowrap !justify-center px-4 py-8 md:px-10 md:py-10">
            <h1 className="font-headline font-bold tracking-tight text-[#111113] text-[clamp(1.9rem,3.4vw,2.75rem)] leading-[1.12] m-0">
              {adminOnly ? ADMIN_HEADING : HEADING}
            </h1>

            <p className="font-body text-sm text-[#4a4753] mt-3 mb-0">
              {adminOnly ? (
                ADMIN_PROMPT
              ) : (
                <>
                  {SIGNUP_PROMPT}{" "}
                  <Link
                    to="/user/join/crypto"
                    style={{ color: ACCENT }}
                    className="underline underline-offset-4 hover:opacity-70 transition-opacity"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </p>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col !flex-nowrap gap-3 mt-8"
            >
              <input
                type="email"
                name="email"
                value={user.email}
                onChange={handleChange}
                placeholder="Email"
                aria-label="Email"
                autoComplete="email"
                required
                className={INPUT_CLASS}
              />

              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={user.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  aria-label="Password"
                  autoComplete="current-password"
                  required
                  className={`${INPUT_CLASS} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 p-1 text-black/40 hover:text-accent-on-light transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              {valid === 2 && (
                <p className="font-body text-[13px] text-[#d92d20] m-0">
                  Enter all fields
                </p>
              )}

              {formError && (
                <p
                  role="alert"
                  className="font-body text-[13px] leading-relaxed rounded-md border border-[#d92d20]/25 bg-[#d92d20]/[0.06] text-[#d92d20] px-4 py-3 m-0"
                >
                  {formError}
                </p>
              )}

              <div className="flex !flex-nowrap !justify-end">
                <Link
                  to="/user/forgot/password"
                  className="font-body text-[13px] text-black/50 hover:text-accent-on-light no-underline transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`${SUBMIT_CLASS} mt-3 disabled:opacity-60 disabled:cursor-not-allowed`}
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-[#1b0a44]/30 border-t-[#1b0a44] animate-spin" />
                    Logging in
                  </>
                ) : (
                  <>
                    <span className="inline-block group-hover:translate-x-1 transition-transform duration-300">
                      Login
                    </span>
                    <span className="material-symbols-outlined text-[18px] group-hover:animate-arrow-slide">
                      arrow_forward
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
